// Site map: the ERP sitemap image with an SVG overlay in image-pixel coordinates.
// Cameras are circles with FOV wedges (bearing clockwise from image-up), devices are squares,
// zones are translucent polygons. The SVG shares the image's viewBox so it scales with it.
// Phase 5 (console): `live` marks the event's speakers (rounded squares, green) and relays (diamonds, amber) with their
// live state (a relay that is on, a speaker that is playing, blinks), and they can be clicked (pick a speaker, open a
// relay).

import { useState } from 'react';
import type { Camera, Device, Placement, Sitemap } from '../api.ts';
import { AuthImg } from './AuthMedia.tsx';

function polar(x: number, y: number, r: number, bearingDeg: number): [number, number] {
  const a = (bearingDeg * Math.PI) / 180;
  return [x + r * Math.sin(a), y - r * Math.cos(a)];
}

function wedgePath(p: Placement): string | null {
  const f = p.fov;
  if (!f || !(f.range_px > 0) || !(f.angle_deg > 0)) return null;
  const angle = Math.min(f.angle_deg, 359.9);
  const [x1, y1] = polar(p.x, p.y, f.range_px, f.bearing_deg - angle / 2);
  const [x2, y2] = polar(p.x, p.y, f.range_px, f.bearing_deg + angle / 2);
  const large = angle > 180 ? 1 : 0;
  return `M${p.x},${p.y} L${x1},${y1} A${f.range_px},${f.range_px} 0 ${large} 1 ${x2},${y2} Z`;
}

const ZONE_COLORS = ['#4CC9F0', '#A193FF', '#F2C14E', '#3DD68C', '#FF8C42'];

export interface SitemapViewProps {
  map: Sitemap;
  cameras: Camera[];
  devices: Device[];
  /** Console: cameras with alarms on the open event (pulsing ring). */
  alarmCameraIds?: ReadonlySet<string>;
  /** Console: the camera in the player. */
  selectedCameraId?: string | null;
  /** Console: click a camera to view it. */
  onCameraClick?: (cameraId: string) => void;
  /** Hides the label/FOV toggles and the footer (narrow context panel). */
  compact?: boolean;
  /** Phase 5: the event's speakers and relays by id, with live state. */
  live?: ReadonlyMap<string, LiveDeviceMark>;
  /** Phase 5: devices to ring (the speakers a talk-down goes to). */
  selectedDeviceIds?: ReadonlySet<string>;
  /** Phase 5: click a speaker or relay. */
  onDeviceClick?: (deviceId: string) => void;
}

export interface LiveDeviceMark {
  kind: 'speaker' | 'relay';
  name: string;
  /** A relay on, a speaker playing. */
  active: boolean;
  /** Relay function (strobe, siren, …). */
  fn?: string | null;
  /** Gate or lock: a human decision. */
  humanOnly?: boolean;
}

export function SitemapView({ map, cameras, devices, alarmCameraIds, selectedCameraId, onCameraClick, compact, live, selectedDeviceIds, onDeviceClick }: SitemapViewProps) {
  const [showLabels, setShowLabels] = useState(!compact);
  const [showFov, setShowFov] = useState(true);
  const [hover, setHover] = useState<string | null>(null);
  const w = map.image?.width_px || 1000;
  const h = map.image?.height_px || 750;
  const unit = Math.max(w, h) / 100; // ~1% of the longer edge
  const camById = new Map(cameras.map((c) => [c.id, c]));
  const devById = new Map(devices.map((d) => [d.id, d]));

  const isCamera = (p: Placement) => p.entity_type === 'camera';
  const camPlacements = map.placements.filter(isCamera);
  const devPlacements = map.placements.filter((p) => !isCamera(p));

  function labelFor(p: Placement): string {
    if (p.label) return p.label;
    if (isCamera(p)) {
      const c = camById.get(p.entity_id);
      return c ? (c.displayNumber !== null ? `C${c.displayNumber}` : c.name) : p.entity_id;
    }
    return devById.get(p.entity_id)?.name ?? live?.get(p.entity_id)?.name ?? p.entity_id;
  }

  function titleFor(p: Placement): string {
    if (isCamera(p)) {
      const c = camById.get(p.entity_id);
      return c ? `${c.name}${c.health ? ` · ${c.health.status}` : ''}` : `Camera ${p.entity_id} (not in this site's camera list)`;
    }
    const lv = live?.get(p.entity_id);
    if (lv) return `${lv.kind === 'speaker' ? 'Speaker' : `Relay${lv.fn ? ` · ${lv.fn}` : ''}`}: ${lv.name}${lv.active ? (lv.kind === 'speaker' ? ' · playing' : ' · ON') : ''}${lv.humanOnly ? ' · human only' : ''}`;
    const d = devById.get(p.entity_id);
    return d ? `${d.kind.replace('_', ' ')}: ${d.name}` : `${p.entity_type === 'device' ? 'Device' : p.entity_type} ${p.label ?? p.entity_id}`;
  }

  // Labels go on the left of markers near the right edge so they stay inside the image.
  const flip = (p: Placement) => p.x > w * 0.75;

  const healthCls = (p: Placement) => {
    const st = camById.get(p.entity_id)?.health?.status;
    return st === 'offline' ? 'off' : st === 'unauthorized' ? 'warn' : '';
  };

  const isAlarm = (p: Placement) => !!alarmCameraIds?.has(p.entity_id);
  const isSel = (p: Placement) => !!selectedCameraId && selectedCameraId === p.entity_id;

  return (
    <div className="sec">
      {compact ? null : <div className="maptools">
        <label className="ck-inline">
          <input type="checkbox" checked={showLabels} onChange={(e) => setShowLabels(e.target.checked)} />
          Labels
        </label>
        <label className="ck-inline">
          <input type="checkbox" checked={showFov} onChange={(e) => setShowFov(e.target.checked)} />
          Fields of view
        </label>
        <span className="sp" />
        <span className="mapkey">
          <span><i className="k-cam" />Camera</span>
          <span><i className="k-dev" />Device</span>
          <span><i className="k-zone" />Zone</span>
        </span>
      </div>}
      <div className="sitemap" style={{ aspectRatio: `${w} / ${h}` }}>
        <AuthImg url={map.imageUrl} alt={`Site map ${map.name}`} className="sitemap-img" fallback="No sitemap image" />
        <svg className="sitemap-ovl" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" role="img" aria-label={`Overlay: ${camPlacements.length} cameras, ${devPlacements.length} devices, ${map.zones.length} zones`}>
          {map.zones.map((z, i) => {
            const color = ZONE_COLORS[i % ZONE_COLORS.length] ?? '#4CC9F0';
            const pts = z.polygon.map(([x, y]) => `${x},${y}`).join(' ');
            const cx = z.polygon.reduce((s, [x]) => s + x, 0) / Math.max(1, z.polygon.length);
            const cy = z.polygon.reduce((s, [, y]) => s + y, 0) / Math.max(1, z.polygon.length);
            return (
              <g key={z.zone_id}>
                <polygon points={pts} fill={color} fillOpacity={0.12} stroke={color} strokeOpacity={0.6} strokeWidth={unit * 0.25} strokeDasharray={`${unit} ${unit * 0.6}`}>
                  <title>Zone: {z.name}</title>
                </polygon>
                {showLabels && (
                  <text x={cx} y={cy} className="m-zl" fontSize={unit * 1.6} fill={color} textAnchor="middle" dominantBaseline="central" paintOrder="stroke" stroke="#070B12" strokeWidth={unit * 0.4}>
                    {z.name}
                  </text>
                )}
              </g>
            );
          })}
          {showFov &&
            camPlacements.map((p) => {
              const d = wedgePath(p);
              return d ? (
                <path
                  key={`fov-${p.entity_id}`}
                  d={d}
                  className={`m-fov ${healthCls(p)} ${hover === p.entity_id || isSel(p) ? 'hl' : ''} ${isAlarm(p) ? 'alarm' : ''}`}
                  strokeWidth={unit * 0.15}
                />
              ) : null;
            })}
          {devPlacements.map((p) => {
            const s = unit * 1.6;
            const lv = live?.get(p.entity_id);
            const kind = lv?.kind ?? devById.get(p.entity_id)?.kind ?? 'other';
            const click = lv && onDeviceClick ? () => onDeviceClick(p.entity_id) : undefined;
            const sel = !!selectedDeviceIds?.has(p.entity_id);
            return (
              <g
                key={`dev-${p.entity_type}-${p.entity_id}`}
                className={`m-devg${lv ? ' live' : ''}${lv?.active ? ' on' : ''}${sel ? ' sel' : ''}${lv?.humanOnly ? ' human' : ''}${click ? ' clickable' : ''}`}
                onMouseEnter={() => setHover(p.entity_id)}
                onMouseLeave={() => setHover(null)}
                onClick={click}
                onKeyDown={click ? (e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), click()) : undefined}
                tabIndex={click ? 0 : undefined}
                role={click ? 'button' : undefined}
                aria-label={click ? titleFor(p) : undefined}
                aria-pressed={click && lv?.kind === 'speaker' ? sel : undefined}
              >
                {sel && <rect x={p.x - s} y={p.y - s} width={s * 2} height={s * 2} rx={s * 0.5} className="m-devsel" strokeWidth={unit * 0.25} />}
                <rect
                  x={p.x - s / 2}
                  y={p.y - s / 2}
                  width={s}
                  height={s}
                  rx={lv?.kind === 'relay' ? s * 0.08 : s * 0.25}
                  transform={lv?.kind === 'relay' ? `rotate(45 ${p.x} ${p.y})` : undefined}
                  className={`m-dev k-${kind}`}
                  strokeWidth={unit * 0.3}
                >
                  <title>{titleFor(p)}</title>
                </rect>
                {showLabels && (
                  <text x={flip(p) ? p.x - s * 0.8 : p.x + s * 0.8} textAnchor={flip(p) ? "end" : "start"} y={p.y} fontSize={unit * 1.4} className="m-lb" dominantBaseline="central" paintOrder="stroke" stroke="#070B12" strokeWidth={unit * 0.4}>
                    {labelFor(p)}
                  </text>
                )}
              </g>
            );
          })}
          {camPlacements.map((p) => {
            const r = unit * 1.2;
            const click = onCameraClick && camById.has(p.entity_id) ? () => onCameraClick(p.entity_id) : undefined;
            return (
              <g
                key={`cam-${p.entity_id}`}
                className={`m-camg ${healthCls(p)} ${isAlarm(p) ? 'alarm' : ''} ${isSel(p) ? 'sel' : ''} ${click ? 'clickable' : ''}`}
                onMouseEnter={() => setHover(p.entity_id)}
                onMouseLeave={() => setHover(null)}
                onClick={click}
                onKeyDown={click ? (e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), click()) : undefined}
                tabIndex={click ? 0 : undefined}
                role={click ? 'button' : undefined}
                aria-label={click ? `View ${titleFor(p)}` : undefined}
              >
                {isAlarm(p) && <circle cx={p.x} cy={p.y} r={r * 1.15} className="ring" strokeWidth={unit * 0.3} />}
                <circle cx={p.x} cy={p.y} r={r} className="m-cam" strokeWidth={unit * 0.3}>
                  <title>{titleFor(p)}</title>
                </circle>
                {showLabels && (
                  <text x={flip(p) ? p.x - r * 1.4 : p.x + r * 1.4} textAnchor={flip(p) ? "end" : "start"} y={p.y} fontSize={unit * 1.4} className="m-lb cam" dominantBaseline="central" paintOrder="stroke" stroke="#070B12" strokeWidth={unit * 0.4}>
                    {labelFor(p)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
      {!compact && (
        <div className="note">
          {map.image ? `${w} × ${h} px · ${map.image.content_type}` : 'Image size unknown'} · {camPlacements.length} camera placements ·{' '}
          {devPlacements.length} device placements · {map.zones.length} zones
        </div>
      )}
    </div>
  );
}
